import { CommonLoggedInDataContext } from 'CommonLoggedInData';
import InvestorContext from 'InvestorContext';
import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdvisorAccess } from '../../context/AdvisorAccessContext';
import AbstractSidebarBase from '../AbstractSidebar';
import SidebarItem from '../Sidebar/SidebarItem';
import EntityAssociationSidebarItem from './EntityAssociationSidebarItem';
import FAQSidebarItem from './FAQSidebarItem';

class UserSidebarBase extends AbstractSidebarBase {
  sidebarItems(): ReturnType<typeof SidebarItem>[] {
    const { location, mobile, investorContext, isAdvisorViewActive } = this.props;
    const expandDueToChildren = this.state.expandDueToChildren;
    const disabled = isAdvisorViewActive || false;

    return [
      <SidebarItem
        key="/"
        to="/"
        label="Home"
        icon="Home"
        selected={location.pathname === '/'}
        expanded={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
        disabled={disabled}
      />,
      <SidebarItem
        key="/opportunities"
        to="/opportunities"
        label="New"
        icon="Funds"
        selected={location.pathname.startsWith('/opportunities')}
        expanded={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
        disabled={disabled}
      >
        <SidebarItem
          to="/opportunities/comingSoon"
          label="Coming Soon"
          selected={location.pathname === '/opportunities/comingSoon'}
          dense
          disabled={disabled}
        />
        <SidebarItem
          to="/opportunities/EOI"
          label="EOI"
          selected={location.pathname === '/opportunities/EOI'}
          dense
          disabled={disabled}
        />
        <SidebarItem
          to="/opportunities/applicationOpen"
          label="Application Open"
          selected={location.pathname === '/opportunities/applicationOpen'}
          dense
          disabled={disabled}
        />
      </SidebarItem>,
      <SidebarItem
        key="/portfolio"
        to="/portfolio"
        label="Portfolio"
        icon="Portfolio"
        selected={location.pathname.startsWith('/portfolio')}
        expanded={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
        badgeCount={investorContext?.unreadUpdatesCount() || 0}
        disabled={disabled}
      >
        <SidebarItem
          to="/portfolio/EOI"
          label="EOI"
          selected={location.pathname === '/portfolio/EOI'}
          dense
          disabled={disabled}
        />
        <SidebarItem
          to="/portfolio/applicationOpen"
          label="Application Open"
          selected={location.pathname === '/portfolio/applicationOpen'}
          dense
          disabled={disabled}
        />
        <SidebarItem
          to="/portfolio/established"
          label="Closed"
          selected={location.pathname === '/portfolio/established'}
          dense
          disabled={disabled}
        />
      </SidebarItem>,

      <SidebarItem
        key="/settings"
        to="/settings"
        label="Settings"
        icon="Settings"
        selected={location.pathname === '/settings'}
        expanded={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
        disabled={disabled}
      />,

      <EntityAssociationSidebarItem
        key="/entity-association"
        expandDueToChildren={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
        count={investorContext?.eligibleInvestorsCount() || 0}
        investorIds={investorContext?.investors?.map((investor) => investor.id)}
        disabled={disabled}
      />,
      <FAQSidebarItem
        key="/faq"
        expandDueToChildren={expandDueToChildren}
        onHoverIntent={this.handleHoverIntent}
        disabled={disabled}
      />,
      ...(mobile
        ? [
            <SidebarItem
              key="/logout"
              to="/logout"
              label="Logout"
              selected={false}
              disabled={disabled}
            />,
          ]
        : []),
    ];
  }

  bottomSidebarItems(): ReturnType<typeof SidebarItem>[] {
    const { location, mobile, hasAdvisorAccess, isAdvisorViewActive } = this.props;
    const expandDueToChildren = this.state.expandDueToChildren;
    // Advisor View item should never be disabled, even when advisor view is active
    const disabled = false;

    return [
      ...(hasAdvisorAccess
        ? [
            <SidebarItem
              key="/advice-view"
              to="/advice-view"
              label="Advisor View"
              icon="Advisor"
              selected={location.pathname === '/advice-view'}
              expanded={expandDueToChildren}
              onHoverIntent={this.handleHoverIntent}
              disabled={disabled}
            />,
          ]
        : []),
      ...(mobile
        ? [
            <SidebarItem
              key="/logout"
              to="/logout"
              label="Logout"
              selected={false}
              disabled={isAdvisorViewActive || false}
            />,
          ]
        : []),
    ];
  }
}

export default function Sidebar({ tone = 'tone4', mobile = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useContext(CommonLoggedInDataContext);
  const investorContext = useContext(InvestorContext);
  const { hasAdvisorAccess, advisedFor } = useAdvisorAccess();

  // Check if advisor view is active (yellow panel visible)
  const isAdvisorViewActive = !!advisedFor;

  console.log('Sidebar render - InvestorContext:', {
    hasInvestors: !!investorContext?.investors,
    investorCount: investorContext?.investors?.length || 0,
    eligibleCount: investorContext?.eligibleInvestorsCount() || 0,
    loading: investorContext?.loading() || false,
    timestamp: Date.now(),
  });

  return (
    <UserSidebarBase
      location={location}
      navigate={navigate}
      context={context!}
      tone={tone}
      mobile={mobile}
      investorContext={investorContext}
      hasAdvisorAccess={hasAdvisorAccess()}
      isAdvisorViewActive={isAdvisorViewActive}
    />
  );
}
